import type { ParsedPairSymbol } from "@cede/types";
import { DEFAULT_UNDEFINED_STRING } from "../constants";

/**
 * Parse a pair symbol into its components.
 * Handles both spot format (BTC/USDT) and perps format (BTC/USDT:USDC).
 *
 * @param pairSymbol - The pair symbol to parse
 * @returns Parsed components { base, quote, settlement? }
 *
 * @example
 * // Spot format
 * parsePairSymbol("BTC/USDT")
 * // => { base: "BTC", quote: "USDT", settlement: undefined }
 *
 * @example
 * // Perps format
 * parsePairSymbol("BTC/USDT:USDC")
 * // => { base: "BTC", quote: "USDT", settlement: "USDC" }
 */
export function parsePairSymbol(pairSymbol: string): ParsedPairSymbol {
  const slashIndex = pairSymbol.indexOf("/");

  // No slash found - invalid pair
  if (slashIndex === -1) {
    return {
      base: DEFAULT_UNDEFINED_STRING,
      quote: DEFAULT_UNDEFINED_STRING,
      settlement: undefined,
    };
  }

  const base = pairSymbol.slice(0, slashIndex);
  const quotePart = pairSymbol.slice(slashIndex + 1);

  // Check if quote has settlement token (e.g., "USDT:USDC")
  const colonIndex = quotePart.indexOf(":");
  let quote: string;
  let settlement: string | undefined;

  if (colonIndex === -1) {
    quote = quotePart;
    settlement = undefined;
  } else {
    quote = quotePart.slice(0, colonIndex);
    settlement = quotePart.slice(colonIndex + 1) || undefined;
  }

  return {
    base: base || DEFAULT_UNDEFINED_STRING,
    quote: quote || DEFAULT_UNDEFINED_STRING,
    settlement,
  };
}

/**
 * Get the base token from a pair symbol.
 * Handles both spot and perps formats.
 *
 * @example
 * getBaseToken("BTC/USDT") // => "BTC"
 * getBaseToken("BTC/USDT:USDC") // => "BTC"
 */
export function getBaseToken(pairSymbol: string): string {
  return parsePairSymbol(pairSymbol).base;
}

/**
 * Get the quote token from a pair symbol.
 * Handles both spot and perps formats, stripping settlement token.
 *
 * @example
 * getQuoteToken("BTC/USDT") // => "USDT"
 * getQuoteToken("BTC/USDT:USDC") // => "USDT"
 */
export function getQuoteToken(pairSymbol: string): string {
  return parsePairSymbol(pairSymbol).quote;
}

/**
 * Get the settlement token from a pair symbol.
 * Returns undefined for spot pairs.
 *
 * @example
 * getSettlementToken("BTC/USDT") // => undefined
 * getSettlementToken("BTC/USDT:USDC") // => "USDC"
 */
export function getSettlementToken(pairSymbol: string): string | undefined {
  return parsePairSymbol(pairSymbol).settlement;
}

/**
 * Check if a pair symbol is a perpetual/futures pair.
 * Returns true if the pair has a settlement token.
 *
 * @example
 * isPerpsPair("BTC/USDT") // => false
 * isPerpsPair("BTC/USDT:USDC") // => true
 */
export function isPerpsPair(pairSymbol: string): boolean {
  return parsePairSymbol(pairSymbol).settlement !== undefined;
}

/**
 * Get the spot equivalent of a pair symbol.
 * Strips settlement token if present.
 *
 * @example
 * toSpotPairSymbol("BTC/USDT:USDC") // => "BTC/USDT"
 * toSpotPairSymbol("BTC/USDT") // => "BTC/USDT"
 */
export function toSpotPairSymbol(pairSymbol: string): string {
  const { base, quote } = parsePairSymbol(pairSymbol);
  return `${base}/${quote}`;
}
